import { AlertCircle, Clock, Loader2, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { updateArtifact } from '@/client/sdk.gen'
import { FileLabelSelector } from '@/components/project/analysis-modal/FileLabelSelector'
import { ProgressBar } from '@/components/project/analysis-modal/ProgressBar'
import type { FileLabel, FileLabelSuggestion } from '@/components/project/analysis-modal/types'
import { getFileIconForArtifact } from '@/components/project/artifact-viewer/fileIcons'
import { UnsupportedFileFeedbackFields } from '@/components/project/UnsupportedFileFeedbackFields'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MAX_ARTIFACT_NAME_LENGTH } from '@/constants/artifacts'
import { useAuth } from '@/context/AuthContext'
import { useSocket } from '@/context/SocketContext'
import { useTranslation } from '@/hooks/useTranslation'
import { isSentryUserFeedbackAvailable } from '@/services/sentry'
import {
	fileTypeDescriptionFromContext,
	submitUnsupportedFileUploadFeedbackFromForm,
} from '@/services/unsupportedFileUploadFeedback'
import {
	captureUnexpectedError,
	extractErrorMessage,
	isErrorHandledByInterceptor,
} from '@/utils/error'
import { splitFilename } from '@/utils/files'
import type { UnsupportedFileUploadContext, UploadFileResult } from '@/utils/uploadErrors'

type UploadStatus = 'uploading' | 'done' | 'failed'
type WizardStep = 'overview' | 'classify' | 'unsupported-feedback'

const TIME_DIMENSION_VALUES = new Set<NonNullable<FileLabel['timeDimension']>>([
	'as_is',
	'to_be',
	'other',
])

function parseSuggestedTimeDimension(
	value: string | undefined
): FileLabel['timeDimension'] | undefined {
	if (value && TIME_DIMENSION_VALUES.has(value as NonNullable<FileLabel['timeDimension']>)) {
		return value as NonNullable<FileLabel['timeDimension']>
	}
	return undefined
}

interface FileState {
	artifactId?: string
	uploadStatus: UploadStatus
	classification: FileLabel
	artifactName: string
	unsupportedContext?: UnsupportedFileUploadContext
	suggestion?: FileLabelSuggestion
	userTouched: boolean
}

interface ArtifactClassificationSuggestedEvent {
	project_id: string
	artifact_id: string
	suggested_time_dimension?: string | null
	suggested_purpose?: string | null
	confidence?: number | null
	rationale?: string | null
	timestamp?: string
}

interface FileClassificationModalProps {
	isOpen: boolean
	onClose: () => void
	files: File[]
	onUploadFile: (file: File) => Promise<UploadFileResult>
	projectId: string
	onClassificationApplied?: () => void
}

const UPLOAD_CONCURRENCY = 10

function findNextIndex(indices: number[], after: number): number | null {
	for (const i of indices) {
		if (i > after) return i
	}
	return null
}

function fileStatusIcon(state: FileState | undefined) {
	if (!state) {
		return <Loader2 className="w-4 h-4 animate-spin text-[#8c52ff] shrink-0" />
	}

	if (state.unsupportedContext) {
		return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
	}

	switch (state.uploadStatus) {
		case 'uploading':
			return <Loader2 className="w-4 h-4 animate-spin text-[#8c52ff] shrink-0" />
		case 'done':
			return <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
		case 'failed':
			return <AlertCircle className="w-4 h-4 text-[#ff3131] shrink-0" />
	}
}

export function FileClassificationModal({
	isOpen,
	onClose,
	files,
	onUploadFile,
	projectId,
	onClassificationApplied,
}: FileClassificationModalProps) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const { socket, connected } = useSocket()
	const [step, setStep] = useState<WizardStep>('overview')
	const [currentFileIndex, setCurrentFileIndex] = useState(0)
	const [fileStates, setFileStates] = useState<Map<number, FileState>>(new Map())
	const [activeFileIndices, setActiveFileIndices] = useState<number[]>([])
	const [isApplying, setIsApplying] = useState(false)
	const [showNoClassifiableHint, setShowNoClassifiableHint] = useState(false)
	const [useCase, setUseCase] = useState('')
	const [willingToShare, setWillingToShare] = useState(false)
	const [shareConsentError, setShareConsentError] = useState(false)
	const [useCaseError, setUseCaseError] = useState(false)
	const uploadsStartedRef = useRef(false)
	const unsupportedToastShownRef = useRef(false)
	const classifyCompletedRef = useRef(false)

	const initializeAndUpload = useCallback(() => {
		if (uploadsStartedRef.current || files.length === 0) return
		uploadsStartedRef.current = true

		const initial = new Map<number, FileState>()
		const initialIndices: number[] = []
		for (let i = 0; i < files.length; i++) {
			initialIndices.push(i)
			initial.set(i, {
				uploadStatus: 'uploading',
				classification: {},
				artifactName: splitFilename(files[i].name).base,
				userTouched: false,
			})
		}
		setFileStates(initial)
		setActiveFileIndices(initialIndices)

		let activeCount = 0
		let nextIndex = 0

		const processNext = () => {
			while (activeCount < UPLOAD_CONCURRENCY && nextIndex < files.length) {
				const i = nextIndex++
				activeCount++
				const file = files[i]
				onUploadFile(file)
					.then((result) => {
						setFileStates((prev) => {
							const next = new Map(prev)
							const existing = next.get(i)
							if (existing) {
								if (result.unsupportedContext) {
									next.set(i, {
										...existing,
										uploadStatus: 'failed',
										unsupportedContext: result.unsupportedContext,
									})
								} else {
									next.set(i, {
										...existing,
										artifactId: result.artifactId,
										uploadStatus: result.artifactId ? 'done' : 'failed',
									})
								}
							}
							return next
						})
					})
					.catch((error) => {
						captureUnexpectedError(error)
						setFileStates((prev) => {
							const next = new Map(prev)
							const existing = next.get(i)
							if (existing) {
								next.set(i, { ...existing, uploadStatus: 'failed' })
							}
							return next
						})
					})
					.finally(() => {
						activeCount--
						processNext()
					})
			}
		}
		processNext()
	}, [files, onUploadFile])

	useEffect(() => {
		if (isOpen && files.length > 0) {
			initializeAndUpload()
		}
	}, [isOpen, files, initializeAndUpload])

	useEffect(() => {
		if (!isOpen) {
			setStep('overview')
			setCurrentFileIndex(0)
			setFileStates(new Map())
			setActiveFileIndices([])
			setShowNoClassifiableHint(false)
			setUseCase('')
			setWillingToShare(false)
			setShareConsentError(false)
			setUseCaseError(false)
			uploadsStartedRef.current = false
			unsupportedToastShownRef.current = false
			classifyCompletedRef.current = false
		}
	}, [isOpen])

	const modalRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (isOpen) {
			modalRef.current?.focus()
		}
	}, [isOpen])

	const classifiableIndices = activeFileIndices.filter(
		(i) => fileStates.get(i)?.uploadStatus === 'done'
	)
	const unsupportedIndices = activeFileIndices.filter(
		(i) => fileStates.get(i)?.unsupportedContext !== undefined
	)
	const hasUnsupportedFeedbackStep = unsupportedIndices.length > 0 && isSentryUserFeedbackAvailable()
	const removableFailedIndices = activeFileIndices.filter((i) => {
		const state = fileStates.get(i)
		if (state?.uploadStatus !== 'failed') return false
		if (hasUnsupportedFeedbackStep && state.unsupportedContext) return false
		return true
	})

	const notifyUnsupportedWithoutSentry = useCallback(() => {
		if (isSentryUserFeedbackAvailable() || unsupportedIndices.length === 0) {
			return
		}
		if (unsupportedToastShownRef.current) {
			return
		}
		unsupportedToastShownRef.current = true
		toast.error(t('unsupportedFileUploadFeedback.toast'))
	}, [t, unsupportedIndices.length])
	const allUploadsSettled =
		activeFileIndices.length > 0 &&
		activeFileIndices.every((i) => {
			const state = fileStates.get(i)
			return state && state.uploadStatus !== 'uploading'
		})

	const goToFirstUnsupportedFeedback = useCallback(() => {
		if (!hasUnsupportedFeedbackStep) return false
		const first = findNextIndex(unsupportedIndices, -1)
		if (first === null) return false
		setCurrentFileIndex(first)
		setStep('unsupported-feedback')
		return true
	}, [hasUnsupportedFeedbackStep, unsupportedIndices])

	useEffect(() => {
		if (step !== 'overview' || !allUploadsSettled) return
		if (classifiableIndices.length > 0) return
		if (goToFirstUnsupportedFeedback()) return
		notifyUnsupportedWithoutSentry()
	}, [
		allUploadsSettled,
		classifiableIndices.length,
		goToFirstUnsupportedFeedback,
		notifyUnsupportedWithoutSentry,
		step,
	])

	const transitionToUnsupportedOrClose = useCallback(() => {
		if (goToFirstUnsupportedFeedback()) {
			return
		}
		notifyUnsupportedWithoutSentry()
		onClose()
	}, [goToFirstUnsupportedFeedback, notifyUnsupportedWithoutSentry, onClose])

	const currentState = fileStates.get(currentFileIndex)
	const classifyPosition = classifiableIndices.indexOf(currentFileIndex) + 1
	const currentUnsupportedContext = currentState?.unsupportedContext

	useEffect(() => {
		if (step !== 'unsupported-feedback' || !currentUnsupportedContext) {
			return
		}

		setUseCase('')
		setWillingToShare(false)
		setShareConsentError(false)
		setUseCaseError(false)
	}, [currentUnsupportedContext, step])

	const updateCurrentClassification = useCallback(
		(label: FileLabel) => {
			setFileStates((prev) => {
				const next = new Map(prev)
				const existing = next.get(currentFileIndex)
				if (existing) {
					next.set(currentFileIndex, {
						...existing,
						classification: label,
						userTouched: true,
					})
				}
				return next
			})
		},
		[currentFileIndex]
	)

	const applySuggestion = useCallback((artifactId: string, suggestion: FileLabelSuggestion) => {
		setFileStates((prev) => {
			const next = new Map(prev)
			for (const [index, state] of next) {
				if (state.artifactId !== artifactId) continue

				// Never clobber a choice the user already made.
				next.set(index, {
					...state,
					suggestion,
					classification: state.userTouched
						? state.classification
						: {
								...state.classification,
								timeDimension:
									parseSuggestedTimeDimension(suggestion.timeDimension) ??
									state.classification.timeDimension,
								purpose: suggestion.purpose ?? state.classification.purpose,
							},
				})
			}
			return next
		})
	}, [])

	useEffect(() => {
		if (!isOpen || !socket || !connected) return

		const handleClassificationSuggested = (data: ArtifactClassificationSuggestedEvent) => {
			if (data.project_id !== projectId || !data.artifact_id) return
			if (!data.suggested_purpose && !data.suggested_time_dimension) return

			applySuggestion(data.artifact_id, {
				timeDimension: data.suggested_time_dimension ?? undefined,
				purpose: data.suggested_purpose ?? undefined,
				confidence: data.confidence ?? undefined,
				rationale: data.rationale ?? undefined,
			})
		}

		socket.on('artifact_classification_suggested', handleClassificationSuggested)
		return () => {
			socket.off('artifact_classification_suggested', handleClassificationSuggested)
		}
	}, [applySuggestion, connected, isOpen, projectId, socket])

	const updateCurrentName = useCallback(
		(name: string) => {
			setFileStates((prev) => {
				const next = new Map(prev)
				const existing = next.get(currentFileIndex)
				if (existing) {
					next.set(currentFileIndex, { ...existing, artifactName: name })
				}
				return next
			})
		},
		[currentFileIndex]
	)

	const applyClassification = useCallback(
		async (index: number) => {
			const state = fileStates.get(index)
			if (!state?.artifactId) return

			const body: Record<string, string | null> = {}

			const originalName = files[index].name
			const { base: originalBase, extension: extFromOriginal } = splitFilename(originalName)
			const trimmed = state.artifactName.trim()
			let name = (trimmed || originalBase || originalName).slice(0, MAX_ARTIFACT_NAME_LENGTH)

			const inferredExtension = extFromOriginal
			if (inferredExtension && !name.toLowerCase().endsWith(inferredExtension.toLowerCase())) {
				name = (name + inferredExtension).slice(0, MAX_ARTIFACT_NAME_LENGTH)
			}
			body.name = name

			if (state.classification.timeDimension) {
				body.time_dimension = state.classification.timeDimension
			}
			if (state.classification.purpose) {
				body.purpose = state.classification.purpose
			}
			if (state.classification.purposeCustom) {
				body.purpose_custom = state.classification.purposeCustom
			}

			try {
				await updateArtifact({
					path: { project_id: projectId, artifact_id: state.artifactId },
					body,
				})
				onClassificationApplied?.()
			} catch (error) {
				captureUnexpectedError(error)
				console.error('Failed to update artifact classification:', error)
				if (!isErrorHandledByInterceptor(error)) {
					toast.error(
						extractErrorMessage(error, t('pages.projectDetail.uploadClassification.classificationFailed'))
					)
				}
			}
		},
		[fileStates, files, onClassificationApplied, projectId, t]
	)

	const handleNext = useCallback(async () => {
		const state = fileStates.get(currentFileIndex)
		if (state?.uploadStatus !== 'done' || !state.artifactId || !state.classification.timeDimension) {
			return
		}
		setIsApplying(true)
		try {
			await applyClassification(currentFileIndex)
		} finally {
			setIsApplying(false)
		}
		const next = findNextIndex(classifiableIndices, currentFileIndex)
		if (next !== null) {
			setCurrentFileIndex(next)
		} else {
			classifyCompletedRef.current = true
			transitionToUnsupportedOrClose()
		}
	}, [
		applyClassification,
		classifiableIndices,
		currentFileIndex,
		fileStates,
		transitionToUnsupportedOrClose,
	])

	const handleSkip = useCallback(() => {
		const next = findNextIndex(classifiableIndices, currentFileIndex)
		if (next !== null) {
			setCurrentFileIndex(next)
		} else {
			classifyCompletedRef.current = true
			transitionToUnsupportedOrClose()
		}
	}, [classifiableIndices, currentFileIndex, transitionToUnsupportedOrClose])

	const handleUnsupportedNext = () => {
		const next = findNextIndex(unsupportedIndices, currentFileIndex)
		if (next !== null) {
			setCurrentFileIndex(next)
			return
		}

		if (!classifyCompletedRef.current) {
			const firstClassifiable = findNextIndex(classifiableIndices, -1)
			if (firstClassifiable !== null) {
				setCurrentFileIndex(firstClassifiable)
				setStep('classify')
				return
			}
		}

		if (!allUploadsSettled) {
			setStep('overview')
			return
		}

		onClose()
	}

	const handleUnsupportedSubmit = () => {
		if (!currentUnsupportedContext) {
			return
		}

		const trimmedUseCase = useCase.trim()
		if (!trimmedUseCase) {
			setUseCaseError(true)
			return
		}

		if (!willingToShare) {
			setShareConsentError(true)
			return
		}

		const eventId = submitUnsupportedFileUploadFeedbackFromForm(
			currentUnsupportedContext,
			{
				fileTypeDescription: fileTypeDescriptionFromContext(
					currentUnsupportedContext,
					t('unsupportedFileUploadFeedback.unknownFileType')
				),
				useCase: trimmedUseCase,
				willingToShare,
			},
			user
		)

		if (!eventId) {
			toast.error(t('unsupportedFileUploadFeedback.submitError'))
			return
		}

		toast.success(t('unsupportedFileUploadFeedback.submitSuccess'))
		handleUnsupportedNext()
	}

	const handleStartClassifying = useCallback(() => {
		setShowNoClassifiableHint(false)
		const first = findNextIndex(classifiableIndices, -1)
		if (first !== null) {
			setCurrentFileIndex(first)
			setStep('classify')
			return
		}

		if (!goToFirstUnsupportedFeedback()) {
			setShowNoClassifiableHint(true)
		}
	}, [classifiableIndices, goToFirstUnsupportedFeedback])

	const handleRemoveFailed = useCallback(() => {
		setActiveFileIndices((prev) => {
			const next = prev.filter((index) => !removableFailedIndices.includes(index))
			const stillCurrent = next.includes(currentFileIndex)
			if (!stillCurrent && next.length > 0) {
				setCurrentFileIndex(next[0])
			}
			if (next.length === 0) {
				setStep('overview')
			}
			return next
		})
		setShowNoClassifiableHint(false)
	}, [currentFileIndex, removableFailedIndices])

	useEffect(() => {
		if (step !== 'classify') return
		if (!classifiableIndices.includes(currentFileIndex)) {
			const next = findNextIndex(classifiableIndices, -1)
			if (next !== null) {
				setCurrentFileIndex(next)
				return
			}

			if (!goToFirstUnsupportedFeedback()) {
				setStep('overview')
				setShowNoClassifiableHint(true)
			}
		}
	}, [classifiableIndices, currentFileIndex, goToFirstUnsupportedFeedback, step])

	if (!isOpen || files.length === 0) return null
	if (typeof document === 'undefined') return null

	const totalSteps = hasUnsupportedFeedbackStep ? 3 : 2
	const currentStepNumber = step === 'overview' ? 1 : step === 'classify' ? 2 : 3

	const overviewStep = (
		<>
			<h2 id="file-classification-title" className="text-lg font-semibold mb-1">
				{t('pages.projectDetail.uploadClassification.filesOverviewTitle') as string}
			</h2>

			<p className="text-sm text-muted-foreground mb-2">
				{t('pages.projectDetail.uploadClassification.filesOverviewDescription') as string}
			</p>

			<p className="text-sm text-muted-foreground mb-4">
				{t('pages.projectDetail.uploadClassification.filesOverviewUploadParallelHint') as string}
			</p>

			<ul className="space-y-2 mb-4">
				{activeFileIndices.map((i) => {
					const file = files[i]
					const state = fileStates.get(i)
					return (
						<li key={i} className="flex items-center gap-2 text-sm">
							{fileStatusIcon(state)}
							<span className="truncate">{file.name}</span>
						</li>
					)
				})}
			</ul>

			{showNoClassifiableHint && (
				<p className="mb-4 text-sm text-[#ff3131]">
					{t('pages.projectDetail.uploadClassification.noClassifiableFiles') as string}
				</p>
			)}

			<div className="flex flex-col gap-2 sm:flex-row">
				<Button
					type="button"
					variant="outline"
					onClick={handleRemoveFailed}
					disabled={removableFailedIndices.length === 0}
					className="flex-1"
				>
					{t('pages.projectDetail.uploadClassification.removeFailedFiles') as string}
				</Button>
				<Button
					type="button"
					onClick={handleStartClassifying}
					disabled={
						activeFileIndices.length === 0 || (classifiableIndices.length === 0 && !allUploadsSettled)
					}
					className="flex-1 bg-[#8c52ff] hover:bg-[#7a3ff0] text-white"
				>
					{classifiableIndices.length === 0 && unsupportedIndices.length > 0
						? t('pages.projectDetail.uploadClassification.continueToFeedback')
						: t('pages.projectDetail.uploadClassification.startClassifying')}
				</Button>
			</div>
		</>
	)

	const sourceFile = files[currentFileIndex]
	const sourceExtension = splitFilename(sourceFile.name).extension
	const FileContextIcon = getFileIconForArtifact(null, sourceExtension || undefined)

	const classifyStep = currentState ? (
		<>
			<h2 id="file-classification-title" className="text-lg font-semibold mb-1">
				{t('pages.projectDetail.uploadClassification.title') as string}
			</h2>

			<p className="text-sm text-muted-foreground mb-4">
				{t('pages.projectDetail.uploadClassification.description') as string}
			</p>

			<div className="mb-4 flex items-center gap-3 rounded-lg border border-border p-3">
				<FileContextIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
				<p className="min-w-0 truncate text-sm font-medium text-foreground">{sourceFile.name}</p>
			</div>

			<div className="mb-4 space-y-2">
				<Label htmlFor="artifact-name">
					{t('pages.projectDetail.uploadClassification.artifactName') as string}
				</Label>

				<Input
					id="artifact-name"
					value={currentState.artifactName}
					onChange={(e) => updateCurrentName(e.target.value)}
					placeholder={splitFilename(sourceFile.name).base}
					className="font-medium"
				/>
			</div>

			<FileLabelSelector
				label={currentState.classification}
				onChange={updateCurrentClassification}
				isContextFile
				suggestion={currentState.suggestion}
			/>

			<div className="flex gap-2 mt-6">
				<Button
					type="button"
					variant="outline"
					onClick={handleSkip}
					disabled={isApplying}
					className="flex-1"
				>
					{t('pages.projectDetail.uploadClassification.skip') as string}
				</Button>

				<Button
					type="button"
					onClick={handleNext}
					disabled={
						isApplying ||
						currentState.uploadStatus !== 'done' ||
						!currentState.classification.timeDimension
					}
					className="flex-1 bg-[#8c52ff] hover:bg-[#7a3ff0] text-white"
				>
					{classifyPosition < classifiableIndices.length
						? (t('pages.projectDetail.uploadClassification.next') as string)
						: (t('pages.projectDetail.uploadClassification.finish') as string)}
				</Button>
			</div>
		</>
	) : null

	const unsupportedFeedbackStep = currentUnsupportedContext ? (
		<>
			<h2 id="file-classification-title" className="text-lg font-semibold mb-1">
				{t('unsupportedFileUploadFeedback.modalStepTitle')}
			</h2>

			<p className="text-sm text-muted-foreground mb-4">
				{t('unsupportedFileUploadFeedback.modalStepDescription')}
			</p>

			<div className="mb-4 flex items-center gap-3 rounded-lg border border-border p-3">
				<FileContextIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
				<p className="min-w-0 truncate text-sm font-medium text-foreground">{sourceFile.name}</p>
			</div>

			<div className="mb-6 space-y-4">
				<UnsupportedFileFeedbackFields
					useCase={useCase}
					onUseCaseChange={(value) => {
						setUseCase(value)
						if (value.trim()) setUseCaseError(false)
					}}
					useCaseError={useCaseError}
					willingToShare={willingToShare}
					onWillingToShareChange={(agreed) => {
						setWillingToShare(agreed)
						if (agreed) setShareConsentError(false)
					}}
					shareConsentError={shareConsentError}
				/>
			</div>

			<div className="flex gap-2">
				<Button type="button" variant="outline" onClick={handleUnsupportedNext} className="flex-1">
					{t('unsupportedFileUploadFeedback.skip')}
				</Button>

				<Button
					type="button"
					onClick={handleUnsupportedSubmit}
					className="flex-1 bg-[#8c52ff] hover:bg-[#7a3ff0] text-white"
				>
					{t('unsupportedFileUploadFeedback.submit')}
				</Button>
			</div>
		</>
	) : null

	const modalContent = (
		<div
			ref={modalRef}
			tabIndex={-1}
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 transition-opacity"
			onKeyDown={(e) => e.key === 'Escape' && onClose()}
			role="dialog"
			aria-modal="true"
			aria-labelledby="file-classification-title"
		>
			<div
				role="document"
				className="mx-4 flex max-h-[90vh] w-full max-w-lg flex-col overflow-x-hidden rounded-2xl bg-background p-5 shadow-lg ring-1 ring-foreground/10"
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			>
				<div className="mb-4 flex items-center justify-between gap-2">
					<div className="flex-1">
						<ProgressBar currentStep={currentStepNumber} totalSteps={totalSteps} />
					</div>

					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="h-8 w-8 rounded-full"
						onClick={onClose}
						aria-label={t('common.close') as string}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>

				{step === 'overview' && overviewStep}
				{step === 'classify' && classifyStep}
				{step === 'unsupported-feedback' && unsupportedFeedbackStep}
			</div>
		</div>
	)

	return createPortal(modalContent, document.body)
}
